import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface SignInFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  loading: boolean;
  onForgotPassword?: (email: string) => Promise<void>;
}

const SignInForm: React.FC<SignInFormProps> = ({
  onSubmit,
  loading,
  onForgotPassword
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(email, password);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onForgotPassword) return;
    
    setIsSubmitting(true);
    setForgotPasswordSuccess(false);
    await onForgotPassword(forgotEmail);
    setIsSubmitting(false);
    setForgotPasswordSuccess(true);
    
    // Keep dialog open to show success message
    // User can close it manually
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          
          {onForgotPassword && (
            <div className="flex justify-end">
              <Button 
                type="button" 
                variant="link" 
                className="text-sm text-blue-500 px-0 py-0 h-auto font-medium"
                onClick={() => setIsDialogOpen(true)}
              >
                Forgot your password?
              </Button>
            </div>
          )}
        </div>
        <Button 
          type="submit" 
          className="w-full bg-glowingTeal hover:bg-glowingTeal/90" 
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>
        
        {onForgotPassword && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setForgotPasswordSuccess(false);
              setForgotEmail('');
            }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset your password</DialogTitle>
                <DialogDescription>
                  Enter your email address below and we'll send you a link to reset your password.
                </DialogDescription>
              </DialogHeader>
              
              {forgotPasswordSuccess && (
                <Alert className="mt-2 bg-green-50 border-green-200">
                  <Info className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-800">
                    If an account exists with this email, we've sent you a password reset link. 
                    Please check your inbox and spam folder.
                  </AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4 py-4">
                <Input
                  type="email"
                  placeholder="Email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
                
                <DialogFooter>
                  <Button 
                    type="submit" 
                    className="w-full bg-glowingTeal hover:bg-glowingTeal/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send reset link'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </form>
    </div>
  );
};

export default SignInForm;
